/**
 * AnnouncementBanner Component
 * Displays active announcements on the dashboard
 */
import { useState, useEffect } from 'react';
import {
  Announcement,
  getActiveAnnouncements,
  dismissAnnouncement
} from '../services/announcementService';
import { formatDate } from '../utils/dateUtils';

interface AnnouncementBannerProps {
  maxVisible?: number;
}

export function AnnouncementBanner({ maxVisible = 5 }: AnnouncementBannerProps) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dismissingId, setDismissingId] = useState<number | null>(null);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await getActiveAnnouncements();
      setAnnouncements(data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (announcementID: number) => {
    try {
      setDismissingId(announcementID);
      await dismissAnnouncement(announcementID);
      // Remove from local state
      setAnnouncements(prev => prev.filter(a => a.announcementID !== announcementID));
    } catch (err) {
      console.error('Failed to dismiss announcement:', err);
    } finally {
      setDismissingId(null);
    }
  };

  const getIcon = (type: string): string => {
    switch (type) {
      case 'Deadline':
        return '\uD83D\uDCC5'; // Calendar
      case 'Audit':
        return '\uD83D\uDD0D'; // Magnifying glass
      case 'Visit':
        return '\uD83C\uDFE2'; // Building
      case 'Event':
        return '\uD83C\uDF89'; // Party popper
      default:
        return '\u2139\uFE0F'; // Info
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'High':
        return '#d32f2f';
      case 'Medium':
        return '#f57c00';
      case 'Low':
        return '#1976d2';
      default:
        return '#757575';
    }
  };

  const getPriorityBgColor = (priority: string): string => {
    switch (priority) {
      case 'High':
        return '#ffebee';
      case 'Medium':
        return '#fff3e0';
      case 'Low':
        return '#e3f2fd';
      default:
        return '#f5f5f5';
    }
  };

  if (loading) {
    return null; // Don't show loading state, just hide
  }

  if (error || announcements.length === 0) {
    return null; // No announcements to show
  }

  const visibleAnnouncements = announcements.slice(0, maxVisible);

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {visibleAnnouncements.map(announcement => (
        <div
          key={announcement.announcementID}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            padding: '1rem',
            marginBottom: '0.75rem',
            backgroundColor: getPriorityBgColor(announcement.priority),
            borderLeft: `4px solid ${getPriorityColor(announcement.priority)}`,
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <span style={{ fontSize: '1.5rem', marginRight: '1rem', lineHeight: 1 }}>
            {getIcon(announcement.announcementType)}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <strong style={{ color: '#333' }}>{announcement.title}</strong>
              <span
                style={{
                  fontSize: '0.7rem',
                  padding: '0.15rem 0.4rem',
                  borderRadius: '3px',
                  backgroundColor: getPriorityColor(announcement.priority),
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'uppercase'
                }}
              >
                {announcement.priority}
              </span>
              <span
                style={{
                  fontSize: '0.7rem',
                  padding: '0.15rem 0.4rem',
                  borderRadius: '3px',
                  backgroundColor: '#e0e0e0',
                  color: '#666'
                }}
              >
                {announcement.announcementType}
              </span>
            </div>
            <p style={{ margin: '0.5rem 0', color: '#555', fontSize: '0.9rem' }}>
              {announcement.message}
            </p>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>
              Posted by {announcement.createdByName} on{' '}
              {formatDate(announcement.createdAt)}
              {announcement.endDate && (
                <span> &middot; Ends {formatDate(announcement.endDate)}</span>
              )}
            </div>
          </div>
          <button
            onClick={() => handleDismiss(announcement.announcementID)}
            disabled={dismissingId === announcement.announcementID}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.2rem',
              color: '#999',
              cursor: 'pointer',
              padding: '0.25rem',
              lineHeight: 1,
              opacity: dismissingId === announcement.announcementID ? 0.5 : 1
            }}
            title="Dismiss announcement"
          >
            &times;
          </button>
        </div>
      ))}
      {announcements.length > maxVisible && (
        <div style={{ textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
          +{announcements.length - maxVisible} more announcements
        </div>
      )}
    </div>
  );
}
